<template>
  <Container>
    <template v-slot:header>Job Tracker</template>
    <template v-slot:content>
      <div class="shadow text-left text-gray-500 mb-5 bg-white">
        <div class="p-2">
          <div class="p-2">
            <span class="font-medium text-gray-700 uppercase text-left">Queue overview</span>
          </div>
          <div class="p-2">
            <span class="font-medium text-gray-600 uppercase text-left">Files: </span><span
            class="text-gray-500 whitespace-nowrap pl-3 text-right">{{ formatN(statsData.jobs_count) }}</span>
          </div>
          <div class="p-2">
          <span class="font-medium text-gray-600 uppercase text-left">Records: </span><span
          class="text-gray-500 whitespace-nowrap pl-3 text-right">{{ formatN(statsData.records_in_queue) }}</span>
          </div>

        </div>
      </div>

      <div class="tab bg-white shadow rounded-t-md p-8 pt-4 hidden-print">
        <nav class="float-left">
          <a
            v-for="tab in tabs"
            :key="tab.name"
            :class="[
              tab.current
                ? 'current'
                : 'border-transparent hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap mr-5 pb-2 px-1 border-b-2 text-md cursor-pointer',
            ]"
            @click="changeTab(tab.name)"
          >
            {{ tab.name }}
          </a>
        </nav>
      </div>

      <section class="flex flex-row-reverse">
        <div class="mb-6 mt-6">
          <SearchInput
            v-model="searchKeyword"
            placeholder="Search by name, email, file name, or job ID"
            sync-model-value
          />
        </div>
      </section>

      <div class="w-full mt-6 overflow-x-auto" v-show="activeTab === 'Time'">
        <div class="align-middle bg-white inline-block min-w-full">
          <table class="divide-gray-300 min-w-full text-left table-auto">
            <thead class="border-b-2">
              <tr>
                <th
                  v-for="columnName in TimecolumnNames"
                  :key="columnName.name"
                  scope="col"
                  :class="`
                px-6
                py-5
                text-left text-xs
                font-medium
                text-gray-500
                uppercase
                tracking-wider
              `"
                >
                  <FilterableHeaderCell
                    :title="columnName.name"
                    :param="columnName.param"
                    @switchOrder="onSwitchOrders(columnName.param)"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr>
                  <td :colspan="TimecolumnNames.length" class="text-center py-10">
                    <PulseLoader color="#ff5a5f" />
                  </td>
                </tr>
              </template>
              <template v-else-if="data?.length">
                <tr v-for="row in data" :key="row.id">
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.id }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500 group relative">
                    <div class="flex items-center">
                      <span class="truncate max-w-xs cursor-pointer"
                        @click="copyToClipboard(row.input_file_name)"
                        :title="row.input_file_name">
                        {{ truncateText(row.input_file_name, 20) }}
                      </span>
                      <button
                        @click.stop="copyToClipboard(row.input_file_name)"
                        class="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-gray-600 flex-shrink-0"
                        title="Copy to clipboard"
                      >
                        <DocumentDuplicateIcon class="h-4" />
                      </button>
                    </div>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.created_by_email }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ serviceMappings[row.actions] || row.actions }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{
                      row?.created_at?.length === 10
                        ? new Date(row.created_at).toLocaleDateString("en-US")
                        : new Date(row.created_at).toLocaleString("en-US")
                    }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ formatN(row.email_count) }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{
                      row?.qms_start_time?.length === 10
                        ? new Date(row.qms_start_time).toLocaleDateString("en-US")
                        : new Date(row.qms_start_time).toLocaleString("en-US")
                    }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.bouncer_time }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.blackbox_time }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.final_assembly }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.total_time }}
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td
                    :colspan="TimecolumnNames.length"
                    class="px-6 py-10 text-sm text-gray-500 text-center"
                  >
                    <template v-if="payload.search">
                      No jobs match &ldquo;{{ payload.search }}&rdquo;.
                      <button
                        @click="clearSearch"
                        class="underline text-gray-600 hover:text-gray-800 ml-1"
                      >
                        Clear search
                      </button>
                    </template>
                    <template v-else>No data</template>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <nav
            class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 sm:rounded-b-lg"
            aria-label="Pagination"
          >
            <div class="flex-1 py-4"></div>
            <div class="hidden sm:block mr-4">
              <p class="text-sm text-right text-gray-700">
                {{ " " }}
                <span class="font-medium">{{ currentPage }}</span>
                {{ " " }}
                of
                {{ " " }}
                <span class="font-medium">{{ totalPages }}</span>
                {{ " " }}
              </p>
            </div>
            <div class="flex justify-between sm:justify-end">
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </nav>
        </div>
      </div>
      <div class="w-full mt-6 overflow-x-auto" v-show="activeTab === 'Impact'">
        <div class="align-middle bg-white inline-block min-w-full">
          <table class="divide-gray-300 min-w-full text-left table-auto">
            <thead class="border-b-2">
              <tr>
                <th
                  v-for="columnName in ImpactcolumnNames"
                  :key="columnName.name"
                  scope="col"
                  :class="`
                px-6
                py-5
                text-left text-xs
                font-medium
                text-gray-500
                uppercase
                tracking-wider
              `"
                >
                  <FilterableHeaderCell
                    :title="columnName.name"
                    :param="columnName.param"
                    @switchOrder="onSwitchOrders(columnName.param)"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr>
                  <td :colspan="ImpactcolumnNames.length" class="text-center py-10">
                    <PulseLoader color="#ff5a5f" />
                  </td>
                </tr>
              </template>
              <template v-else-if="data?.length">
                <tr v-for="row in data" :key="row.id">
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.id }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500 group relative">
                    <div class="flex items-center">
                      <span class="truncate max-w-xs cursor-pointer"
                        @click="copyToClipboard(row.input_file_name)"
                        :title="row.input_file_name">
                        {{ truncateText(row.input_file_name, 20) }}
                      </span>
                      <button
                        @click.stop="copyToClipboard(row.input_file_name)"
                        class="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-gray-600 flex-shrink-0"
                        title="Copy to clipboard"
                      >
                        <DocumentDuplicateIcon class="h-4" />
                      </button>
                    </div>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.created_by_email }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ serviceMappings[row.actions] || row.actions }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{
                      row?.created_at?.length === 10
                        ? new Date(row.created_at).toLocaleDateString("en-US")
                        : new Date(row.created_at).toLocaleString("en-US")
                    }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ formatN(row.email_count) }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.kb }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.real_time }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.bouncer }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.final_count }}
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td
                    :colspan="ImpactcolumnNames.length"
                    class="px-6 py-10 text-sm text-gray-500 text-center"
                  >
                    <template v-if="payload.search">
                      No jobs match &ldquo;{{ payload.search }}&rdquo;.
                      <button
                        @click="clearSearch"
                        class="underline text-gray-600 hover:text-gray-800 ml-1"
                      >
                        Clear search
                      </button>
                    </template>
                    <template v-else>No data</template>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <nav
            class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 sm:rounded-b-lg"
            aria-label="Pagination"
          >
            <div class="flex-1 py-4"></div>
            <div class="hidden sm:block mr-4">
              <p class="text-sm text-right text-gray-700">
                {{ " " }}
                <span class="font-medium">{{ currentPage }}</span>
                {{ " " }}
                of
                {{ " " }}
                <span class="font-medium">{{ totalPages }}</span>
                {{ " " }}
              </p>
            </div>
            <div class="flex justify-between sm:justify-end">
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </nav>
        </div>
      </div>
      <div class="w-full mt-6 overflow-x-auto" v-show="activeTab === 'Queue'">
        <div class="align-middle bg-white inline-block min-w-full">
          <table class="divide-gray-300 min-w-full text-left table-auto">
            <thead class="border-b-2">
              <tr>
                <th
                  v-for="columnName in ProcessingFilescolumnNames"
                  :key="columnName.name"
                  scope="col"
                  :class="`
                px-6
                py-5
                text-left text-xs
                font-medium
                text-gray-500
                uppercase
                tracking-wider
              `"
                >
                  <FilterableHeaderCell
                    :title="columnName.name"
                    :param="columnName.param"
                    @switchOrder="onSwitchOrders(columnName.param)"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isLoading">
                <tr>
                  <td :colspan="ProcessingFilescolumnNames.length" class="text-center py-10">
                    <PulseLoader color="#ff5a5f" />
                  </td>
                </tr>
              </template>
              <template v-else-if="data?.length">
                <tr v-for="row in data" :key="row.id">
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.id }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500 group relative">
                    <div class="flex items-center">
                      <span class="truncate max-w-xs cursor-pointer"
                        @click="copyToClipboard(row.input_file_name)"
                        :title="row.input_file_name">
                        {{ truncateText(row.input_file_name, 20) }}
                      </span>
                      <button
                        @click.stop="copyToClipboard(row.input_file_name)"
                        class="ml-2 opacity-0 group-hover:opacity-100 transition-opacity text-gray-500 hover:text-gray-600 flex-shrink-0"
                        title="Copy to clipboard"
                      >
                        <DocumentDuplicateIcon class="h-4" />
                      </button>
                    </div>
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ row.created_by_email }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ serviceMappings[row.actions] || row.actions }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{
                      row?.created_at?.length === 10
                        ? new Date(row.created_at).toLocaleDateString("en-US")
                        : new Date(row.created_at).toLocaleString("en-US")
                    }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ formatN(row.email_count) }}
                  </td>
                  <td class="px-6 py-5 text-sm text-gray-500">
                    {{ capitalize(row.status) }}
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr>
                  <td
                    :colspan="ProcessingFilescolumnNames.length"
                    class="px-6 py-10 text-sm text-gray-500 text-center"
                  >
                    <template v-if="payload.search">
                      No jobs match &ldquo;{{ payload.search }}&rdquo;.
                      <button
                        @click="clearSearch"
                        class="underline text-gray-600 hover:text-gray-800 ml-1"
                      >
                        Clear search
                      </button>
                    </template>
                    <template v-else>No data</template>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <nav
            class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 sm:rounded-b-lg"
            aria-label="Pagination"
          >
            <div class="flex-1 py-4"></div>
            <div class="hidden sm:block mr-4">
              <p class="text-sm text-right text-gray-700">
                {{ " " }}
                <span class="font-medium">{{ currentPage }}</span>
                {{ " " }}
                of
                {{ " " }}
                <span class="font-medium">{{ totalPages }}</span>
                {{ " " }}
              </p>
            </div>
            <div class="flex justify-between sm:justify-end">
              <button
                @click="goToPage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                @click="goToPage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </nav>
        </div>
      </div>
      <div></div>
    </template>
  </Container>
</template>

<script>
import Container from "@/components/common/Container";
import apiMixin from "@/mixins/apiMixin";
import { ref } from "vue";
import FilterableHeaderCell from "@/components/common/FilterableHeaderCell";
import humanize from "humanize";
import PulseLoader from "vue-spinner/src/PulseLoader";
import { DocumentDuplicateIcon } from "@heroicons/vue/outline";
import SearchInput from "@/components/common/SearchInput";

export default {
  name: "Bouncer",
  mixins: [apiMixin],
  components: {
    Container,
    FilterableHeaderCell,
    PulseLoader,
    DocumentDuplicateIcon,
    SearchInput,
  },
  data() {
    return {
      TimecolumnNames: ref([
        { name: "Job ID", param: "id" },
        { name: "File Name", param: "input_file_name" },
        { name: "Created By", param: "created_by_email" },
        { name: "Service", param: "actions" },
        { name: "Created At", param: "created_at" },
        { name: "Email Count", param: "email_count" },
        { name: "QMS start time", param: "qms_start_time" },
        { name: "Bouncer Time", param: "bouncer_time" },
        { name: "Blackbox Time", param: "blackbox_time" },
        { name: "Final Assembly", param: "final_assembly" },
        { name: "Total time", param: "total_time" },
      ]),
      ImpactcolumnNames: ref([
        { name: "Job ID", param: "id" },
        { name: "File Name", param: "input_file_name" },
        { name: "Created By", param: "created_by_email" },
        { name: "Service", param: "actions" },
        { name: "Created At", param: "created_at" },
        { name: "Email Count", param: "email_count" },
        { name: "KB", param: "kb" },
        { name: "Real-Time", param: "real_time" },
        { name: "Bouncer/ Blackbox ", param: "bouncer" },
        // { name: "Blackbox", param: "blackbox" },
        { name: "Final", param: "final_count" },
      ]),
      ProcessingFilescolumnNames: ref([
        { name: "Job ID", param: "id" },
        { name: "File Name", param: "input_file__name" },
        { name: "Created By", param: "created_by__email" },
        { name: "Service", param: "actions" },
        { name: "Created At", param: "created_at" },
        { name: "Email Count", param: "emails_count" },
        { name: "Status", param: "status" }
      ]),
      data: ref([]),
      statsData: ref([]),
      payload: {
        page: 1,
        size: 15,
        ordering: "-created_at",
        search: "",
      },
      isLoading:false,
      currentPage: 1,
      totalPages: 1,
      searchKeyword: "",
      tabs: [{ name: "Time", current: true },{ name: "Impact", current: false },{ name: "Queue", current: false }],
      activeTab: "Time",
      serviceMappings : {
                evaluation: "Evaluation",
                threat_evaluation: "Threat Evaluation",
                verification: "Deliverability + Threat Detection",
                deliverability: "Deliverability Check",
                threat_assessment: "Threat Detection",
                real_time: "Real Time"
                }
    };
  },
  mounted() {
    this.JobTrackerStats();
    this.JobTrackerTime()
  },
  watch: {
    // SearchInput sets inheritAttrs: false, so an @input listener on it is
    // silently dropped -- watch the debounced model it emits instead.
    searchKeyword() {
      this.onSearchInput();
    },
  },
  methods: {
    truncateText(text, length) {
      if (text?.length > length) {
        return text.slice(0, length) + '...';
      }
      return text;
    },
    changeTab(tabName) {
      this.tabs.forEach((tab) => {
        tab.current = tab.name === tabName;
      });
      this.activeTab = tabName;
      this.payload.page = 1;
      this.currentPage = 1;
      this.payload.ordering = "-created_at";
      this.refetchActiveTab();
    },
    refetchActiveTab() {
      if (this.activeTab === "Impact") return this.JobTrackerImpact();
      if (this.activeTab === "Queue") return this.JobTrackerProcessingFiles();
      return this.JobTrackerTime();
    },
    onSearchInput() {
      this.payload.search = this.searchKeyword;
      this.payload.page = 1;
      this.currentPage = 1;
      this.refetchActiveTab();
    },
    clearSearch() {
      this.searchKeyword = "";
    },
    formatN(count) {
      return humanize.numberFormat(count, 0);
    },
    capitalize(string){
      if(string){
        return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
      }
    },
    async onSwitchOrders(name) {
      this.payload.ordering = this.payload.ordering == name ? `-${name}` : name;
      this.payload.page = 1;
      this.currentPage = 1;
      this.refetchActiveTab();
    },
    async JobTrackerTime() {
      this.isLoading = true;
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () => await this.$alfredService.JobTrackerTime(this.payload),
      });
      if (response) {
        this.data = response.results;
        this.totalPages = Math.max(1, Math.ceil(response.count / this.payload.size));
      }
      this.isLoading = false;
    },
    async JobTrackerImpact() {
      this.isLoading = true;
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () => await this.$alfredService.JobTrackerImpact(this.payload),
      });
      if (response) {
        this.data = response.results;
        this.totalPages = Math.max(1, Math.ceil(response.count / this.payload.size));
      }
      this.isLoading = false;
    },
    async JobTrackerStats() {
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () => await this.$alfredService.JobTrackerStats(),
      });
      if (response) {
        this.statsData = response;
      }
    },
    async JobTrackerProcessingFiles() {
      this.isLoading = true;
      const response = await this.$store.dispatch("fetching/FETCH", {
        apiCall: async () => await this.$alfredService.JobTrackerProcessingFiles(this.payload),
      });
      if (response) {
        this.data = response.results;
        this.totalPages = Math.max(1, Math.ceil(response.count / this.payload.size));
      }
      this.isLoading = false;
    },
    goToPage(pageNumber) {
      if (pageNumber >= 1 && pageNumber <= this.totalPages) {
        this.payload.page = pageNumber;
        this.currentPage = pageNumber;
        this.refetchActiveTab();
      }
    },
    formatString(str) {
      if (str) {
        return str
          .replace(/['[\]]/g, "")
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).catch(console.error);
    }
  },
};
</script>

<style scoped>
.current {
  border-color: var(--main-color);
  color: var(--main-color);
}
select {
  background-image: none;
  border-color: #ffffff00;
}
</style>
